import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import StudentPhoto from "../../components/StudentPhoto";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs } from "../../api/examApi";
import { listStudents } from "../../api/studentApi";
import { getAdmitCardInstruction } from "../../api/admitCardInstructionApi";
import { listExamRoutineGenerate } from "../../api/examRoutineGenerateApi";
import { cleanText } from "../../utils/textUtils";
import "../Attendance/Attendance.css";
import "./AdmitCard.css";

const academicYearApi = createLookupApi("academic-years");
const groupApi = createLookupApi("groups");

/**
 * Inline equivalent of digicampusbd's `admit-card-sear-plan-download` ("Exam
 * admit Card") — one printable card per student in a class/roll range for a
 * chosen exam (name, photo, class/section/shift/group, roll, registration
 * no., guardian mobile, plus that exam's instructions from
 * `AdmitCardInstructionPage`). The old page's 3 buttons ("Admit Card"/"Admit
 * Card2"/"Admit Card3") each download a different cosmetic Jasper PDF layout
 * of the *exact same* `AdmitAndSeatPlanResponse` data (confirmed by reading
 * `AdmitSeatPlanService.downloadStudentAdmitCard` directly — same student
 * query, same fields, only the `.jasper` template path differs) — per this
 * project's established pattern (tabulation sheet, marksheet), this is one
 * browser-printable view instead of 3 near-identical PDF downloads. Reuses
 * `GET /api/students` (already built) for the roster — no new student data
 * endpoint needed, only the new `AdmitCardInstruction` lookup.
 *
 * Also serves `admit-card-with-exam-routine` ("Admit With Routine") — read
 * `downloadStudentAdmitCardWithRoutineClassSectionPrintView` directly: it's
 * the SAME roster + instructions as above, plus one extra ingredient (each
 * exam's real per-subject date/time/room schedule for the selected group,
 * from `ExamRoutineGenerate` — the module this url was originally deferred
 * for, now built; see MIGRATION.md) and one extra filter (a specific group,
 * since the routine itself is defined per class+group, not per section).
 * Notably the old app's own `/jasper/...`-named endpoint for this url
 * already returns plain JSON (not a PDF) — the old template builds the
 * printable card client-side from that JSON — so this url was already an
 * inline view in the old app itself, just reached via a Jasper-style path.
 */
export default function AdmitCardPage() {
  const { pageUrl } = useParams();
  const showRoutine = pageUrl === "admit-card-with-exam-routine";

  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [fromRoll, setFromRoll] = useState("");
  const [toRoll, setToRoll] = useState("");

  const [students, setStudents] = useState([]);
  const [instruction, setInstruction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [routine, setRoutine] = useState([]);

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
    });
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    if (showRoutine) groupApi.list().then(setGroups);
  }, [showRoutine]);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
  }, [classConfigurationId, classConfigs]);

  useEffect(() => {
    if (groups.length > 0 && !groupId) setGroupId(String(groups[0].id));
  }, [groups]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!examConfigId) {
      setInstruction(null);
      return;
    }
    getAdmitCardInstruction(examConfigId).then(setInstruction).catch(() => setInstruction(null));
  }, [examConfigId]);

  useEffect(() => {
    if (!showRoutine) return;
    const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
    const ec = examConfigs.find((x) => String(x.examConfigId) === examConfigId);
    if (!cc || !ec || !groupId || !academicYear) {
      setRoutine([]);
      return;
    }
    listExamRoutineGenerate(academicYear, ec.examId, cc.classId, [groupId])
      .then(setRoutine)
      .catch(() => setRoutine([]));
  }, [showRoutine, classConfigs, classConfigurationId, examConfigs, examConfigId, groupId, academicYear]);

  useEffect(() => {
    if (!classConfigurationId || !academicYear) {
      setStudents([]);
      return;
    }
    setLoading(true);
    setError("");
    let cancelled = false;
    listStudents(academicYear, classConfigurationId)
      .then((data) => {
        if (!cancelled) setStudents(data);
      })
      .catch(() => setError("Failed to load students."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [classConfigurationId, academicYear]);

  const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
  const examName = examConfigs.find((ec) => String(ec.examConfigId) === examConfigId)?.examName || "";

  const rows = useMemo(() => {
    const from = fromRoll ? Number(fromRoll) : 0;
    const to = toRoll ? Number(toRoll) : 0;
    return students
      .filter((s) => (from === 0 || to === 0 ? true : s.studentRoll >= from && s.studentRoll <= to))
      .filter((s) => !showRoutine || !groupId || String(s.groupId) === groupId)
      .slice()
      .sort((a, b) => a.studentRoll - b.studentRoll);
  }, [students, fromRoll, toRoll, showRoutine, groupId]);

  return (
    <MainLayout>
      <div className="page-header no-print">
        <h1>{showRoutine ? "Admit With Routine" : "Exam Admit Card"}</h1>
      </div>

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters no-print">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section List
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam List
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
        {showRoutine && (
          <label>
            Select Group
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          From Roll
          <input type="number" value={fromRoll} onChange={(e) => setFromRoll(e.target.value)} />
        </label>
        <label>
          To Roll
          <input type="number" value={toRoll} onChange={(e) => setToRoll(e.target.value)} />
        </label>
        <button type="button" className="btn-secondary" onClick={() => window.print()} disabled={rows.length === 0}>
          Print
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : rows.length === 0 ? (
        <p>No students found for this selection.</p>
      ) : (
        <div className="admit-card-grid">
          {rows.map((s) => (
            <div className="admit-card" key={s.identificationId}>
              <div className="admit-card-header">
                <strong>Admit Card</strong>
                <span>{examName}</span>
              </div>
              <div className="admit-card-body">
                <StudentPhoto className="admit-card-photo" fileName={s.imageName} alt={s.studentName} />
                <div className="admit-card-fields">
                  <div>
                    <span className="admit-card-label">Name:</span> {s.studentName}
                  </div>
                  <div>
                    <span className="admit-card-label">Student ID:</span> {s.customStudentId}
                  </div>
                  <div>
                    <span className="admit-card-label">Class:</span> {cc ? cc.classConfigName : ""}
                  </div>
                  <div>
                    <span className="admit-card-label">Group:</span> {s.groupName && s.groupName !== "N/A" ? s.groupName : "-"}
                  </div>
                  <div>
                    <span className="admit-card-label">Roll:</span> {s.studentRoll}
                  </div>
                  <div>
                    <span className="admit-card-label">Registration No:</span> {cleanText(s.registrationNo, "-")}
                  </div>
                  <div>
                    <span className="admit-card-label">Father:</span> {s.fatherName}
                  </div>
                  <div>
                    <span className="admit-card-label">Mother:</span> {s.motherName}
                  </div>
                  <div>
                    <span className="admit-card-label">Guardian Mobile:</span> {s.guardianMobile}
                  </div>
                </div>
              </div>
              {showRoutine && routine.length > 0 && (
                <table className="admit-card-routine-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Subject</th>
                      <th>Time</th>
                      <th>Session</th>
                      <th>Room</th>
                    </tr>
                  </thead>
                  <tbody>
                    {routine.map((r) => (
                      <tr key={r.examRoutineId}>
                        <td>{r.examDate}</td>
                        <td>{r.subjectName}</td>
                        <td>{r.examTime}</td>
                        <td>{r.examSession}</td>
                        <td>{r.roomNo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {instruction && (
                <ol className="admit-card-instructions">
                  {[instruction.instructionOne, instruction.instructionTwo, instruction.instructionThree, instruction.instructionFour]
                    .filter((v) => v && v.trim())
                    .map((v, i) => (
                      <li key={i}>{v}</li>
                    ))}
                </ol>
              )}
            </div>
          ))}
        </div>
      )}
    </MainLayout>
  );
}
